import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc, addDoc, serverTimestamp, where, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiPlus, FiFile, FiDownload, FiEye, FiSearch, FiEdit2, FiX, FiCheck, FiMail, FiAlertCircle, FiFileText, FiCornerUpRight, FiChevronDown, FiTrash2 } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { sendEmailsWithErrorHandling, validateEmails } from '../../../../api/emailService';
import { formatCCTVEmailBody, formatCCTVPurchaseOrderEmail, generatePurchaseOrderNumber } from '../email/cctvEmailService';
import Table, { Column } from '../../../../components/Table'; // Import Table component
import { fetchCompaniesData } from '../../../../screens/companies/service/companiesService';
import { fetchBranchesData } from '../../../branches/service/branchesService';
import CCTVPsoRequest from './CCTVPsoRequest';
import { isPsoHeadDesignation } from '../../../../utils/psoDesignation';


type InventoryItem = {
    id: string;
    itemCode?: string;
    itemName?: string;
    itemDescription?: string;
    brandName?: string;
    makeModel?: string;
    rates?: number;
    [key: string]: any;
};

type SelectedItem = {
    itemId: string;
    itemCode: string;
    itemName: string;
    itemDescription?: string;
    quantity: number;
};

type CCTVRequest = {
    id: string;
    requestId?: string;
    poNumber?: string;
    dateOfRequest?: string;
    branchCode?: string;
    branchName?: string;
    branchId?: string;
    vendor?: string;
    vendorId?: string;
    coveredArea?: string;
    floorsConsistOn?: string;
    items?: SelectedItem[];
    remarks?: string;
    requestCategory?: 'Replacement' | 'Additional' | 'New';
    status?: string;
    psoStatus?: 'installed' | 'not installed';
    installationDate?: string;
    cancelReason?: string;
    cancelledAt?: any;
    cancelledBy?: string;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    [key: string]: any;
};

export default function CCTV() {
    const pso = useAppSelector((s) => s.user.pso);
    const [activeTab, setActiveTab] = useState<'po' | 'pso'>('pso');
    const [referencedPsoRequest, setReferencedPsoRequest] = useState<any | null>(null);
    const [showPsoSelectModal, setShowPsoSelectModal] = useState(false);
    const [psoSearchTerm, setPsoSearchTerm] = useState('');
    const [approvedPsoRequests, setApprovedPsoRequests] = useState<any[]>([]);

    const [requests, setRequests] = useState<CCTVRequest[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<CCTVRequest[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');
    const [startDate, setStartDate] = useState('');

    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<CCTVRequest | null>(null);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [showToast, setShowToast] = useState(false);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editedRequest, setEditedRequest] = useState<CCTVRequest | null>(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [taxPercentage, setTaxPercentage] = useState<number>(() => {
        const saved = localStorage.getItem('cctv_tax_percentage');
        return saved ? parseFloat(saved) : 18;
    });
    const [selectedVendor, setSelectedVendor] = useState<any>(null);
    const [showInstallConfirmModal, setShowInstallConfirmModal] = useState(false);
    const [installationDate, setInstallationDate] = useState(new Date().toISOString().split('T')[0]);
    const [processingAction, setProcessingAction] = useState<{ id: string, type: 'email' | 'pdf' } | null>(null);

    // Form states
    const [formData, setFormData] = useState({
        requestId: '',
        poNumber: '',
        dateOfRequest: new Date().toISOString().split('T')[0],
        branchCode: '',
        branchName: '',
        branchId: '',
        vendor: '',
        vendorId: '',
        selectedItems: [] as SelectedItem[],
        remarks: '',
        requestCategory: '' as 'Replacement' | 'Additional' | 'New' | '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vendors, setVendors] = useState<any[]>([]);
    const [deployments, setDeployments] = useState<any[]>([]);
    const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
    const senderEmail = useMemo(() => pso?.email || auth.currentUser?.email || '', [pso]);
    const branchPsoEmailHint = useMemo(() => {
        if (!selectedRequest) return '';
        if (selectedRequest.branch?.psoEmail) return selectedRequest.branch.psoEmail;
        const branch = branches.find(
            (b) => b.id === selectedRequest.branchId || b.branchCode === selectedRequest.branchCode
        ) as any;
        return branch?.psoEmail || '';
    }, [branches, selectedRequest]);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [branchSearch, setBranchSearch] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');
    const [itemSearch, setItemSearch] = useState('');
    const [itemBrandFilter, setItemBrandFilter] = useState<string>('All');
    const [tempSelectedItems, setTempSelectedItems] = useState<{ [key: string]: InventoryItem }>({});
    const branchModalRef = useRef<HTMLDivElement>(null);
    const vendorModalRef = useRef<HTMLDivElement>(null);

    const uid = auth.currentUser?.uid;

    // Check if user can create requests
    // Regular PSO can create requests, but for incharge users, only Incharge MIS and Incharge CCTV can create
    const designation = pso?.designation || '';
    const isIncharge = /in[ -]?charge/i.test(designation);
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isInchargeMIS = inchargeType.includes('mis');
    const isInchargeCCTV = inchargeType.includes('cctv');
    const canCreateRequests = isIncharge; // Only incharge users can create requests
    const psoDesignation = pso?.designation || '';
    const psoName = pso?.name || '';
    const isRegionalPSO = psoDesignation === 'Regional PSO';
    const isHead = isPsoHeadDesignation(pso?.designation);
    const isPSOUser = sessionStorage.getItem('role') === 'pso';

    // Get current PSO ID for filtering branches
    const currentPsoId = pso?.authUid || uid || '';

    // Check if user can edit (PSO or CCTV Incharge)
    const canEdit = psoDesignation === 'PSO' || psoDesignation?.toLowerCase().includes('cctv');

    // Only incharge users (MIS or CCTV) can approve/reject requests
    const canApproveReject = isInchargeMIS || isInchargeCCTV;

    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const usePurchaseOrderTemplate = isIncharge || isBankUser;

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
        const isBankUser = userRole === 'bank';

        const fetchCompanyId = async () => {
            if (isBankUser) {
                // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                const directBank = await getDoc(doc(db, "banks", uid));
                if (directBank.exists()) {
                    setCompanyId(uid);
                    return;
                }
                // Try query by userId field
                const banksSnap = await getDocs(collection(db, "banks"));
                for (const bankDoc of banksSnap.docs) {
                    const bankData = bankDoc.data();
                    if (bankData.userId === uid) {
                        setCompanyId(bankDoc.id);
                        return;
                    }
                }
            } else {
                // For PSO users, find which bank has this PSO
                const banksSnap = await getDocs(collection(db, 'banks'));
                for (const bankDoc of banksSnap.docs) {
                    const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
                    const posDoc = await getDoc(posDocRef);
                    if (posDoc.exists()) {
                        setCompanyId(bankDoc.id);
                        break;
                    }
                }
            }
        };

        fetchCompanyId();
    }, [uid]);

    useEffect(() => {
        if (isPSOUser) {
            setActiveTab('pso');
        }
    }, [isPSOUser]);

    // Fetch branches
    useEffect(() => {
        if (!uid || !companyId) return;
        const fetchBranches = async () => {
            try {
                const userRole = sessionStorage.getItem('role');
                const isBankUser = userRole === 'bank';
                const { branches: branchesData } = await fetchBranchesData(
                    uid,
                    isBankUser,
                    pso?.authUid,
                    pso?.designation
                );
                setBranches(branchesData);
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };
        fetchBranches();
    }, [uid, companyId, pso]);

    // Fetch deployments
    useEffect(() => {
        if (!companyId) return;
        const fetchDeployments = async () => {
            try {
                const deploymentsSnap = await getDocs(collection(db, 'banks', companyId, 'companyDeployment'));
                const deploymentsList = deploymentsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setDeployments(deploymentsList);
            } catch (error) {
                console.error('Error fetching deployments:', error);
            }
        };
        fetchDeployments();
    }, [companyId]);

    // Fetch vendors (CCTV companies) - both from global companies collection and bank-specific companies
    useEffect(() => {
        if (!uid) return;

        const fetchVendors = async () => {
            try {
                const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
                const isBankUser = userRole === 'bank';

                // Use the same function as Companies.tsx to fetch all companies
                // This fetches from both banks/{bankId}/companies (manually added) and global companies collection
                const allCompanies = await fetchCompaniesData(uid, isBankUser);

                // Filter by companyType === 'CCTV' to get only CCTV vendors
                const cctvVendors = allCompanies.filter(company => company.companyType === 'CCTV');

                // Transform to vendor format expected by the component
                const vendorsList = cctvVendors.map(company => ({
                    ...company,
                    // Ensure companyId is set (use existing companyId or fallback to id)
                    companyId: company.companyId || company.id,
                }));

                setVendors(vendorsList);
                console.log(`Fetched ${vendorsList.length} CCTV vendors (including manually added vendors)`);
            } catch (error) {
                console.error('Error fetching vendors:', error);
                setToastMessage('Failed to fetch vendors');
                setToastType('error');
                setShowToast(true);
            }
        };

        fetchVendors();
    }, [uid]);

    // Fetch inventory items
    useEffect(() => {
        if (!companyId) return;
        const fetchInventory = async () => {
            try {
                const itemsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'inventory'),
                        orderBy('createdAt', 'desc')
                    )
                );
                const itemsList = itemsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as InventoryItem[];
                setInventoryItems(itemsList);
            } catch (error) {
                console.error('Error fetching inventory items:', error);
            }
        };
        fetchInventory();
    }, [companyId]);

    // Generate PO Number: PObranchCodeDateMonth
    const generatePoNumber = async (branchCode: string) => {
        if (!branchCode) return '';

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const basePo = `PO${branchCode}${dd}${mm}`;

        let finalePo = basePo;
        let suffix = 0;
        let isUnique = false;

        while (!isUnique && suffix < 100) {
            const currentCheck = suffix === 0 ? basePo : `${basePo}-${suffix}`;

            // Check if this poNumber already exists in Firestore
            const q = query(
                collection(db, 'banks', companyId || '', 'hardware_requests'),
                where('poNumber', '==', currentCheck)
            );
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                finalePo = currentCheck;
                isUnique = true;
            } else {
                suffix++;
            }
        }

        return finalePo;
    };

    // Generate Request ID (Keeping as secondary ID)
    const generateRequestId = () => {
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `CCTV-${psoInitials}-${timestamp}`;
    };

    // Auto-generate Request ID when modal opens
    useEffect(() => {
        if (showAddModal && !formData.requestId) {
            setFormData(prev => ({ ...prev, requestId: generateRequestId() }));
        }
    }, [showAddModal, psoName]);


    // Close modals on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (branchModalRef.current && !branchModalRef.current.contains(event.target as Node)) {
                setShowBranchModal(false);
            }
            if (vendorModalRef.current && !vendorModalRef.current.contains(event.target as Node)) {
                setShowVendorModal(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset form
    const resetForm = () => {
        setFormData({
            requestId: generateRequestId(),
            poNumber: '',
            dateOfRequest: new Date().toISOString().split('T')[0],
            branchCode: '',
            branchName: '',
            branchId: '',
            vendor: '',
            vendorId: '',
            selectedItems: [],
            remarks: '',
            requestCategory: '',
        });
        setTempSelectedItems({});
    };

    // Helper function to get deployed vendor from deployments collection
    const getDeployedVendorForBranch = (branchId: string) => {
        const deployment = deployments.find(d => d.branchId === branchId);
        if (!deployment) return null;

        const deployedVendor = vendors.find(v =>
            v.id === deployment.companiesId ||
            v.companyId === deployment.companiesId
        );

        if (deployedVendor) {
            return {
                vendorId: deployedVendor.companyId || deployedVendor.id,
                vendor: deployedVendor.companyName || ''
            };
        }
        return null;
    };

    // Helper function to get vendor info from branch's cctvVendorId
    const getVendorFromBranch = (branch: any) => {
        if (!branch.cctvVendorId) {
            return null;
        }

        // Find the vendor from the vendors list
        const deployedVendor = vendors.find(v =>
            v.id === branch.cctvVendorId ||
            v.companyId === branch.cctvVendorId
        );

        if (deployedVendor) {
            return {
                vendorId: deployedVendor.companyId || deployedVendor.id,
                vendor: deployedVendor.companyName || ''
            };
        }

        return null;
    };

    // Handle branch selection
    const handleBranchSelection = async (branch: any) => {
        const branchCode = branch.branchCode || '';

        // Generate PO Number immediately when branch is selected
        const generatedPo = await generatePoNumber(branchCode);

        const updatedFormData: any = {
            branchId: branch.id,
            branchCode: branchCode,
            branchName: branch.address || branch.name || '',
            poNumber: generatedPo,
        };

        // Check if branch has a deployed CCTV vendor and auto-select it
        const vendorInfo = getDeployedVendorForBranch(branch.id) || getVendorFromBranch(branch);
        if (vendorInfo) {
            updatedFormData.vendorId = vendorInfo.vendorId;
            updatedFormData.vendor = vendorInfo.vendor;
            console.log('Auto-selected deployed CCTV vendor:', vendorInfo.vendor);
        } else {
            // No deployed vendor - clear vendor selection so user can select
            updatedFormData.vendorId = '';
            updatedFormData.vendor = '';
        }

        setFormData(prev => ({ ...prev, ...updatedFormData }));
        setShowBranchModal(false);
        setBranchSearch('');
    };

    // Handle vendor selection - store companyId if available (for global companies), otherwise use id (for manually added vendors)
    const handleVendorSelection = (vendor: any) => {
        setFormData(prev => ({
            ...prev,
            // Use companyId if available (links to global companies collection), otherwise use id (from bank's companies collection)
            vendorId: vendor.companyId || vendor.id,
            vendor: vendor.companyName || '',
        }));
        setShowVendorModal(false);
        setVendorSearch('');
    };

    // Handle item selection in modal (temporary)
    const handleItemToggle = (item: InventoryItem) => {
        setTempSelectedItems(prev => {
            if (prev[item.id]) {
                const newState = { ...prev };
                delete newState[item.id];
                return newState;
            } else {
                return { ...prev, [item.id]: item };
            }
        });
    };

    // Handle submit selected items from modal
    const handleSubmitSelectedItems = () => {
        const newItems: SelectedItem[] = Object.values(tempSelectedItems).map(item => ({
            itemId: item.id,
            itemCode: item.itemCode || 'N/A',
            itemName: item.itemName || item.itemDescription || 'N/A',
            itemDescription: item.itemDescription || '',
            quantity: 1, // Default quantity is 1
        }));

        setFormData(prev => ({
            ...prev,
            selectedItems: [...prev.selectedItems, ...newItems]
        }));

        // Reset temp selections and close modal
        setTempSelectedItems({});
        setItemSearch('');
        setItemBrandFilter('All');
        setShowItemModal(false);
    };

    // Handle opening item modal
    const handleOpenItemModal = () => {
        setTempSelectedItems({});
        setItemSearch('');
        setItemBrandFilter('All');
        setShowItemModal(true);
    };

    // Handle quantity change for selected items
    const handleQuantityChange = (index: number, quantity: number) => {
        if (quantity < 1) {
            setToastMessage('Quantity must be at least 1');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // If editing, update editedRequest, otherwise update formData
        if (isEditing && editedRequest) {
            setEditedRequest({
                ...editedRequest,
                items: (editedRequest.items || []).map((item, i) =>
                    i === index ? { ...item, quantity } : item
                )
            });
        } else {
            setFormData(prev => ({
                ...prev,
                selectedItems: prev.selectedItems.map((item, i) =>
                    i === index ? { ...item, quantity } : item
                )
            }));
        }
    };

    // Remove selected item
    const handleRemoveItem = (index: number) => {
        // If editing, update editedRequest, otherwise update formData
        if (isEditing && editedRequest) {
            setEditedRequest({
                ...editedRequest,
                items: (editedRequest.items || []).filter((_, i) => i !== index)
            });
        } else {
            setFormData(prev => ({
                ...prev,
                selectedItems: prev.selectedItems.filter((_, i) => i !== index)
            }));
        }
    };

    // Handle save request
    const handleSaveRequest = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.branchCode || !formData.branchName || !formData.vendor || formData.selectedItems.length === 0 || !formData.requestCategory) {
            setToastMessage('Please fill in all required fields (including Request Category) and add at least one item');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        try {
            // Use existing PO Number if present, otherwise generate (fallback)
            const generatedPo = formData.poNumber || await generatePoNumber(formData.branchCode);

            // Double check for duplicate PO Number
            const existingRequestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    where('poNumber', '==', generatedPo),
                    where('requestType', '==', 'CCTV')
                )
            );

            if (!existingRequestsSnap.empty) {
                setToastMessage(`A hardware request with PO Number "${generatedPo}" already exists. Please try another branch or refresh.`);
                setToastType('error');
                setShowToast(true);
                setIsSubmitting(false);
                return;
            }

            // Check for duplicate requests within 24 hours with same branch and items (skipped when creating PO from approved PSO request)
            if (!referencedPsoRequest) {
                const twentyFourHoursAgo = new Date();
                twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

                // Fetch all CCTV requests (without orderBy to avoid index requirement)
                const recentRequestsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'hardware_requests'),
                        where('requestType', '==', 'CCTV')
                    )
                );

                // Normalize items for comparison (sort by itemId and quantity)
                const normalizeItems = (items: any[]) => {
                    return items
                        .map(item => ({
                            itemId: item.itemId || item.itemCode || item.itemName || '',
                            quantity: item.quantity || 0
                        }))
                        .sort((a, b) => (a.itemId || '').localeCompare(b.itemId || ''));
                };

                const normalizedNew = normalizeItems(formData.selectedItems);

                // Check if any recent request (within 24 hours) has the same branch and items
                // Exclude cancelled requests from duplicate check
                for (const docSnap of recentRequestsSnap.docs) {
                    const requestData = docSnap.data();
                    const createdAt = requestData.createdAt?.toDate ? requestData.createdAt.toDate() : new Date(requestData.createdAt);

                    // Skip cancelled requests - they don't count as duplicates
                    if (requestData.status === 'cancelled') {
                        continue;
                    }

                    // Only check requests within 24 hours and same branch
                    if (createdAt && createdAt >= twentyFourHoursAgo && requestData.branchId === formData.branchId) {
                        const existingItems = requestData.items || [];
                        const normalizedExisting = normalizeItems(existingItems);

                        // Check if items are the same (same items with same quantities)
                        if (normalizedExisting.length === normalizedNew.length &&
                            normalizedExisting.every((item, index) =>
                                item.itemId === normalizedNew[index].itemId &&
                                item.quantity === normalizedNew[index].quantity
                            )) {
                            setToastMessage(`A similar hardware request with the same branch and items was created within the last 24 hours. Please verify if this is a duplicate request.`);
                            setToastType('error');
                            setShowToast(true);
                            setIsSubmitting(false);
                            return;
                        }
                    }
                }
            }

            const requestData = {
                requestId: formData.requestId,
                poNumber: generatedPo,
                requestType: 'CCTV',
                requestCategory: formData.requestCategory || 'New',
                dateOfRequest: formData.dateOfRequest,
                branchCode: formData.branchCode,
                branchName: formData.branchName,
                branchId: formData.branchId,
                vendor: formData.vendor,
                vendorId: formData.vendorId,
                items: formData.selectedItems,
                remarks: formData.remarks,
                status: 'PO Created',
                psoStatus: 'not installed',
                companyId: companyId,
                createdBy: uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                referencedPsoRequestId: referencedPsoRequest ? referencedPsoRequest.id : null,
                referencedPsoRequestIdText: referencedPsoRequest ? referencedPsoRequest.requestId : null,
            };

            await addDoc(collection(db, 'banks', companyId, 'hardware_requests'), requestData);

            if (referencedPsoRequest) {
                await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', referencedPsoRequest.id), {
                    isPOGenerated: true,
                    poCreatedBy: uid,
                    poCreatedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                setReferencedPsoRequest(null);
            }

            setToastMessage('CCTV hardware request created successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            resetForm();

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV' && request.docType !== 'pso_request') as CCTVRequest[];
            setRequests(requestsList);
        } catch (error) {
            console.error('Error creating request:', error);
            setToastMessage('Failed to create request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filtered branches, vendors, and items
    const filteredBranches = branches.filter((b: any) => {
        // Incharge users can see all branches
        if (isIncharge) {
            // Only filter by search term for incharge users
            if (branchSearch) {
                const searchLower = branchSearch.toLowerCase();
                return (
                    b.branchCode?.toLowerCase().includes(searchLower) ||
                    b.name?.toLowerCase().includes(searchLower) ||
                    b.address?.toLowerCase().includes(searchLower)
                );
            }
            return true;
        }

        // For regular PSO users, filter by PSO ID - only show branches assigned to this PSO
        if (b.psoId && currentPsoId && b.psoId !== currentPsoId) {
            return false;
        }

        // Then filter by search term
        if (branchSearch) {
            const searchLower = branchSearch.toLowerCase();
            return (
                b.branchCode?.toLowerCase().includes(searchLower) ||
                b.name?.toLowerCase().includes(searchLower) ||
                b.address?.toLowerCase().includes(searchLower)
            );
        }

        return true;
    });

    const filteredVendors = vendors.filter(v =>
        v.companyName?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
        v.companyId?.toLowerCase().includes(vendorSearch.toLowerCase())
    );

    // Get unique brand names from inventory items
    const uniqueBrands = Array.from(new Set(inventoryItems.map(item => item.brandName).filter((brand): brand is string => Boolean(brand))));

    const availableItems = inventoryItems.filter(item => {
        // Check if item is already selected in formData (for add modal) or editedRequest (for edit modal)
        const isAlreadySelectedInForm = formData.selectedItems.some(selected => selected.itemId === item.id);
        const isAlreadySelectedInEdit = (editedRequest?.items || []).some(selected => selected.itemId === item.id);
        const isAlreadySelected = isAlreadySelectedInForm || isAlreadySelectedInEdit;
        const matchesSearch = !itemSearch ||
            item.itemCode?.toLowerCase().includes(itemSearch.toLowerCase()) ||
            item.itemName?.toLowerCase().includes(itemSearch.toLowerCase()) ||
            item.itemDescription?.toLowerCase().includes(itemSearch.toLowerCase());
        const matchesBrand = itemBrandFilter === 'All' || item.brandName === itemBrandFilter;
        return !isAlreadySelected && matchesSearch && matchesBrand;
    });

    // Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const fetchRequests = async () => {
            setLoading(true);
            try {
                // Fetch from hardware_requests collection, not regular requests
                const requestsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'hardware_requests'),
                        orderBy('createdAt', 'desc')
                    )
                );

                const requestsList = requestsSnap.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    .filter((request: any) => request.requestType === 'CCTV' && request.docType !== 'pso_request') as CCTVRequest[];

                setRequests(requestsList);

                // Extract unique statuses
                const uniqueStatuses = Array.from(
                    new Set(requestsList.map(req => req.status).filter((s): s is string => Boolean(s)))
                );
                setStatuses(uniqueStatuses);
            } catch (error) {
                console.error('Error fetching requests:', error);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, [companyId]);

    // Set default filter status to 'pending' for Regional PSO
    useEffect(() => {
        if (psoDesignation === 'Regional PSO') {
            setStatusFilter('pending');
        }
    }, [psoDesignation]);

    // Filter requests
    useEffect(() => {
        let filtered = [...requests];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(request => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    request.poNumber?.toLowerCase().includes(searchLower) ||
                    request.requestId?.toLowerCase().includes(searchLower) ||
                    request.branchCode?.toLowerCase().includes(searchLower) ||
                    request.branchName?.toLowerCase().includes(searchLower) ||
                    request.vendor?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(request => request.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== 'All' && dateFilter !== '') {
            const now = new Date();
            filtered = filtered.filter(request => {
                if (!request.createdAt) return false;
                const requestDate = request.createdAt.toDate ? request.createdAt.toDate() : new Date(request.createdAt);

                switch (dateFilter) {
                    case 'Daily':
                        return requestDate.toDateString() === now.toDateString();
                    case 'Weekly':
                        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        return requestDate >= weekAgo;
                    case 'Monthly':
                        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                        return requestDate >= monthAgo;
                    case 'Custom':
                        const start = startDate ? new Date(startDate) : null;
                        const end = endDate ? new Date(endDate) : null;
                        if (start) start.setHours(0, 0, 0, 0);
                        if (end) {
                            end.setHours(23, 59, 59, 999);
                        }

                        if (start && end) return requestDate >= start && requestDate <= end;
                        if (start) return requestDate >= start;
                        if (end) return requestDate <= end;
                        return true;
                    default:
                        return true;
                }
            });
        }

        setFilteredRequests(filtered);
    }, [requests, searchTerm, statusFilter, dateFilter, startDate, endDate]);

    const handleViewDetails = (request: CCTVRequest) => {
        setSelectedRequest(request);
        setEditedRequest({ ...request });
        setIsEditing(false);
        setShowDetailsModal(true);
    };

    const handleDeleteRequest = async (request: CCTVRequest) => {
        if (!companyId || isActionLoading) return;
        if (!window.confirm("Are you sure you want to permanently delete this CCTV Request?")) return;
        
        setIsActionLoading(true);
        try {
            await deleteDoc(doc(db, 'banks', companyId, 'hardware_requests', request.id));
            setToastMessage("CCTV Request deleted successfully");
            setToastType("success");
            setShowToast(true);
            setShowDetailsModal(false);
            
            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV' && request.docType !== 'pso_request') as CCTVRequest[];
            setRequests(requestsList);
        } catch (error) {
            console.error("Error deleting request:", error);
            setToastMessage("Failed to delete CCTV Request");
            setToastType("error");
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleEdit = () => {
        if (!canEdit) {
            setToastMessage('You do not have permission to edit hardware requests');
            setToastType('error');
            setShowToast(true);
            return;
        }
        if (selectedRequest) {
            setEditedRequest({ ...selectedRequest });
        }
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (selectedRequest) {
            setEditedRequest({ ...selectedRequest });
        }
    };

    const handleApprove = async (requestOverride?: CCTVRequest) => {
        const request = requestOverride || selectedRequest;
        if (!companyId || !request || !uid || isActionLoading) return;

        setProcessingAction({ id: request.id, type: 'email' });
        try {
            console.log('=== PREPARE EMAIL MODAL ===');
            console.log('Selected Request:', request);
            console.log('VendorId from Request:', request.vendorId);
            console.log('Vendor Name from Request:', request.vendor);

            setSelectedRequest(request);
            setEditedRequest({ ...request });
            setIsActionLoading(true);

            const branchEmailFromRequest =
                request.branch?.psoEmail ||
                (branches.find(
                    (b: any) => b.id === request.branchId || b.branchCode === request.branchCode
                ) as any)?.psoEmail ||
                '';
            if (branchEmailFromRequest) {
                console.log(`📨 Branch PSO email resolved: ${branchEmailFromRequest}`);
            }

            // Fetch vendor details for email BEFORE approval
            // Try global companies collection first, then bank's companies collection
            if (request.vendorId) {
                let vendorData: any = null;
                let vendorDocId: string = '';

                try {
                    // First, try to fetch from global companies collection
                    console.log('Fetching vendor from path: companies/' + request.vendorId);
                    const globalVendorDoc = await getDoc(doc(db, 'companies', request.vendorId));

                    if (globalVendorDoc.exists()) {
                        vendorData = globalVendorDoc.data();
                        vendorDocId = globalVendorDoc.id;
                        console.log('✓ Vendor found in global companies collection');
                    } else if (companyId) {
                        // If not found in global, try bank's companies collection (for manually added vendors)
                        console.log('Vendor not in global collection, trying banks/' + companyId + '/companies/' + request.vendorId);
                        const bankVendorDoc = await getDoc(doc(db, 'banks', companyId, 'companies', request.vendorId));
                        if (bankVendorDoc.exists()) {
                            vendorData = bankVendorDoc.data();
                            vendorDocId = bankVendorDoc.id;
                            console.log('✓ Vendor found in bank companies collection');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching vendor:', error);
                    console.error('Error details:', JSON.stringify(error, null, 2));
                }

                if (vendorData) {
                    // Verify it's a CCTV company (if companyType is available)
                    if (vendorData.companyType && vendorData.companyType !== 'CCTV') {
                        console.warn('⚠️ Company found but companyType is not CCTV:', vendorData.companyType);
                    }

                    // Log vendor data for debugging
                    console.log('=== VENDOR DATA ===');
                    console.log('Vendor Document ID:', vendorDocId);
                    console.log('Vendor Data (Full):', JSON.stringify(vendorData, null, 2));

                    const vendorInfo: any = {
                        ...vendorData,
                        id: vendorDocId,
                        companyId: vendorData.companyId || vendorDocId,
                        companyName: vendorData.companyName || request.vendor,
                        contactPersonName: vendorData.contactPersonName,
                        contactEmail: vendorData.contactEmail,
                        contactMobileNumber: vendorData.contactMobileNumber,
                    };

                    // Use company name from request if available (as it was selected during add)
                    if (request.vendor) {
                        console.log('Using company name from request:', request.vendor);
                        vendorInfo.companyName = request.vendor;
                    }

                    console.log('=== FINAL VENDOR INFO FOR EMAIL ===');
                    console.log('Final Vendor Info (Full):', JSON.stringify(vendorInfo, null, 2));
                    setSelectedVendor(vendorInfo);
                } else {
                    console.warn('✗ Vendor document not found for vendorId:', request.vendorId);
                }
            } else {
                console.warn('✗ No vendorId in selectedRequest:', request);
            }

            // Wait a bit for state to update (React state is async)
            await new Promise(resolve => setTimeout(resolve, 100));

            // Initialize email recipients with vendor email and PSO creator email
            const initialRecipients: string[] = [];

            // Fetch vendor details for email BEFORE approval
            // Try global companies collection first, then bank's companies collection
            if (request.vendorId) {
                let selectedVendor: any = null;
                try {
                    console.log('🔍 Searching for vendor with ID/companyId:', request.vendorId);

                    // 1. Try global companies collection by direct ID first
                    const globalVendorDoc = await getDoc(doc(db, 'companies', request.vendorId));
                    if (globalVendorDoc.exists()) {
                        selectedVendor = { id: globalVendorDoc.id, ...globalVendorDoc.data() };
                        console.log('✅ Found global vendor by direct ID:', selectedVendor.companyName);
                    } else {
                        // 2. Try global companies collection by companyId field (e.g. DL-0001)
                        const globalQuery = query(collection(db, 'companies'), where('companyId', '==', request.vendorId));
                        const globalSnap = await getDocs(globalQuery);

                        if (!globalSnap.empty) {
                            selectedVendor = { id: globalSnap.docs[0].id, ...globalSnap.docs[0].data() };
                            console.log('✅ Found global vendor by companyId field:', selectedVendor.companyName);
                        } else if (companyId) {
                            // 3. Try bank-specific companies collection by direct ID
                            const bankVendorDoc = await getDoc(doc(db, 'banks', companyId, 'companies', request.vendorId));
                            if (bankVendorDoc.exists()) {
                                selectedVendor = { id: bankVendorDoc.id, ...bankVendorDoc.data() };
                                console.log('✅ Found bank vendor by direct ID:', selectedVendor.companyName);
                            } else {
                                // 4. Try bank-specific companies collection by companyId field
                                const bankQuery = query(collection(db, 'banks', companyId, 'companies'), where('companyId', '==', request.vendorId));
                                const bankSnap = await getDocs(bankQuery);

                                if (!bankSnap.empty) {
                                    selectedVendor = { id: bankSnap.docs[0].id, ...bankSnap.docs[0].data() };
                                    console.log('✅ Found bank vendor by companyId field:', selectedVendor.companyName);
                                }
                            }
                        }
                    }

                    if (!selectedVendor) {
                        console.log('❌ Vendor not found in any collection for ID:', request.vendorId);
                    } else if (!selectedVendor.companyAddress && !selectedVendor.address) {
                        console.warn('⚠️ Vendor found but ADDRESS IS MISSING in Firestore object:', selectedVendor);
                    }

                    if (selectedVendor) {
                        setSelectedVendor(selectedVendor);
                        if (selectedVendor.contactEmail) {
                            initialRecipients.push(selectedVendor.contactEmail);
                        }
                        if (selectedVendor.email && selectedVendor.email !== selectedVendor.contactEmail) {
                            initialRecipients.push(selectedVendor.email);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching vendor email:', error);
                }
            }

            // Add PSO creator email
            if (request.createdBy) {
                try {
                    const creatorDoc = await getDoc(doc(db, 'banks', companyId, 'pos', request.createdBy));
                    if (creatorDoc.exists()) {
                        const creatorData = creatorDoc.data();
                        const creatorEmail = creatorData.email;
                        const creatorName = creatorData.name || request.createdBy;
                        if (creatorEmail && !initialRecipients.includes(creatorEmail)) {
                            initialRecipients.push(creatorEmail);
                        }
                        console.log(`📧 PSO Email found: ${creatorEmail} (Created by ${creatorName})`);
                    } else {
                        console.log(`❌ PSO document not found for UID: ${request.createdBy} in bank: ${companyId}`);
                    }
                } catch (error) {
                    console.error('Error fetching PSO creator email:', error);
                }
            }

            // Add branch PSO email
            if (branchEmailFromRequest && !initialRecipients.includes(branchEmailFromRequest)) {
                initialRecipients.push(branchEmailFromRequest);
            }

            // Add sender email
            if (senderEmail && !initialRecipients.includes(senderEmail)) {
                initialRecipients.push(senderEmail);
            }

            const uniqueRecipients = Array.from(new Set(initialRecipients));
            console.log('📬 Initial recipients list:', uniqueRecipients);
            setEmailRecipients(uniqueRecipients);
            setEmailInput('');

            // Show email modal - approval will happen after email is sent
            setShowEmailModal(true);
            setIsActionLoading(false); // Stop loading when modal opens
        } catch (error) {
            console.error('Error preparing email:', error);
            setToastMessage('Failed to prepare email');
            setToastType('error');
            setShowToast(true);
            setIsActionLoading(false);
        } finally {
            setProcessingAction(null);
        }
    };

    const handleReject = () => {
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleConfirmReject = async () => {
        if (!companyId || !selectedRequest || !uid || !rejectReason.trim() || isActionLoading) {
            if (!rejectReason.trim()) {
                setToastMessage('Please provide a reason for rejection');
                setToastType('error');
                setShowToast(true);
            }
            return;
        }

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                status: 'rejected',
                rejectReason: rejectReason.trim(),
                rejectedAt: serverTimestamp(),
                rejectedBy: uid,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request rejected successfully');
            setToastType('success');
            setShowToast(true);
            setShowRejectModal(false);
            setRejectReason('');

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error rejecting request:', error);
            setToastMessage('Failed to reject request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleMarkAsPoCreated = async () => {
        if (!companyId || !selectedRequest || !uid || isActionLoading) return;

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                status: 'PO Created',
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Status updated to PO Created successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV' && request.docType !== 'pso_request') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error updating status to PO Created:', error);
            setToastMessage('Failed to update status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!companyId || !selectedRequest || !uid || !cancelReason.trim() || isActionLoading) {
            if (!cancelReason.trim()) {
                setToastMessage('Please provide a reason for cancellation');
                setToastType('error');
                setShowToast(true);
            }
            return;
        }

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                status: 'cancelled',
                cancelReason: cancelReason.trim(),
                cancelledAt: serverTimestamp(),
                cancelledBy: uid,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request cancelled successfully');
            setToastType('success');
            setShowToast(true);
            setShowCancelModal(false);
            setCancelReason('');
            setIsEditing(false);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error cancelling request:', error);
            setToastMessage('Failed to cancel request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && emailInput.trim()) {
            e.preventDefault();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const trimmedEmail = emailInput.trim();
            if (emailRegex.test(trimmedEmail)) {
                if (!emailRecipients.includes(trimmedEmail)) {
                    setEmailRecipients([...emailRecipients, trimmedEmail]);
                }
                setEmailInput('');
            } else {
                setToastMessage('Please enter a valid email address');
                setToastType('error');
                setShowToast(true);
            }
        }
    };

    const removeEmailRecipient = (emailToRemove: string) => {
        setEmailRecipients(emailRecipients.filter(email => email !== emailToRemove));
    };


    const handleSendEmail = async () => {
        if (!selectedRequest || !companyId) {
            setToastMessage('Missing required information');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSendingEmail(true);
        try {
            // Get branch's assigned PSO email and details
            let branchPsoEmail: string | null = branchPsoEmailHint || null;
            let branchPsoName: string = '';
            let branchPsoRegion: string = '';

            if (selectedRequest.branchId && companyId) {
                try {
                    const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', selectedRequest.branchId));
                    if (branchDoc.exists()) {
                        const branchData = branchDoc.data();
                        const branchPsoId = branchData.psoId;

                        if (branchPsoId && typeof branchPsoId === 'string') {
                            const branchPsoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', branchPsoId));
                            if (branchPsoDoc.exists()) {
                                const branchPsoData = branchPsoDoc.data();
                                branchPsoEmail = branchPsoData.email || null;
                                branchPsoName = branchPsoData.name || '';

                                // Extract region from designation
                                const designation = branchPsoData.designation || '';
                                if (designation.includes('North')) {
                                    branchPsoRegion = 'North Region';
                                } else if (designation.includes('South')) {
                                    branchPsoRegion = 'South Region';
                                } else if (designation.includes('East')) {
                                    branchPsoRegion = 'East Region';
                                } else if (designation.includes('West')) {
                                    branchPsoRegion = 'West Region';
                                } else if (designation.includes('Central')) {
                                    branchPsoRegion = 'Central Region';
                                } else {
                                    branchPsoRegion = designation || 'Region';
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branch PSO email:', error);
                }
            }

            // Use current recipient list as-is (respect manual removals)
            const allRecipients = [...emailRecipients];

            if (allRecipients.length === 0) {
                setToastMessage('Please add at least one recipient email address');
                setToastType('error');
                setShowToast(true);
                setIsSendingEmail(false);
                return;
            }

            let emailBody: string;
            let subject: string;

            if (usePurchaseOrderTemplate) {
                // For incharge and bank users, use purchase order format
                const poNumber = selectedRequest.poNumber || selectedRequest.requestId || generatePurchaseOrderNumber();
                const poDate = selectedRequest.createdAt?.toDate ? selectedRequest.createdAt.toDate() : new Date();

                // Get incharge info
                const inchargeInfo = {
                    name: psoName || (isBankUser ? 'Bank User' : 'Incharge'),
                    email: pso?.email || (isBankUser ? auth.currentUser?.email : '') || ''
                };

                // Get bank info for email
                let bankData: any = null;
                if (companyId) {
                    try {
                        const bankDoc = await getDoc(doc(db, 'banks', companyId));
                        if (bankDoc.exists()) {
                            bankData = bankDoc.data();
                        }
                    } catch (error) {
                        console.error('Error fetching bank data:', error);
                    }
                }

                emailBody = formatCCTVPurchaseOrderEmail(selectedRequest, selectedVendor, branches, inchargeInfo, poNumber, poDate, inventoryItems, branchPsoName, branchPsoRegion, taxPercentage, bankData);
                subject = `CCTV Purchase Order - ${poNumber} - ${selectedRequest.branchName || 'N/A'}`;
            } else {
                // For regular PSO/Regional, use regular email format
                let approvingPsoName = 'PSO';
                if (uid && companyId) {
                    try {
                        const psoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', uid));
                        if (psoDoc.exists()) {
                            const psoData = psoDoc.data();
                            approvingPsoName = psoData.name || 'PSO';
                        }
                    } catch (error) {
                        console.error('Error fetching PSO name:', error);
                    }
                }

                // Get bank info for email
                let bankData: any = null;
                if (companyId) {
                    try {
                        const bankDoc = await getDoc(doc(db, 'banks', companyId));
                        if (bankDoc.exists()) {
                            bankData = bankDoc.data();
                        }
                    } catch (error) {
                        console.error('Error fetching bank data:', error);
                    }
                }

                emailBody = formatCCTVEmailBody(selectedRequest, selectedVendor, branches, bankData);
                subject = `CCTV Hardware Request - ${selectedRequest.poNumber || selectedRequest.requestId || selectedRequest.id} - ${selectedRequest.branchName || 'N/A'}`;
            }

            let bankNameForEmail = 'Bank Support';
            if (companyId) {
                try {
                    const bankDoc = await getDoc(doc(db, 'banks', companyId));
                    if (bankDoc.exists()) {
                        const bankDataDoc = bankDoc.data() as { companyName?: string };
                        if (bankDataDoc?.companyName) {
                            bankNameForEmail = `${bankDataDoc.companyName} `;
                        }
                    }
                } catch (error) {
                    console.error('Error fetching bank company name:', error);
                }
            }

            const emailData = {
                to: allRecipients,
                subject: subject,
                body: emailBody,
                isHtml: true,
                companyName: bankNameForEmail
            };

            const results = await sendEmailsWithErrorHandling(emailData);

            let resultMessage = "";
            if (results.successful.length > 0) {
                resultMessage += `Successfully sent to ${results.successful.length} recipient(s): ${results.successful.map(r => r.email).join(', ')}`;
            }

            if (results.failed.length > 0) {
                if (resultMessage) resultMessage += "\n\n";
                resultMessage += `Failed to send to ${results.failed.length} recipient(s):\n`;
                results.failed.forEach(failure => {
                    resultMessage += `• ${failure.email}: ${failure.error}\n`;
                });
            }

            // Only approve if email was sent successfully and user is not incharge
            if (results.failed.length === 0 && results.successful.length > 0 && !isIncharge) {
                try {
                    if (uid) {
                        const { updateDoc, serverTimestamp } = await import('firebase/firestore');
                        await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                            status: 'approved',
                            updatedAt: serverTimestamp(),
                            updatedBy: uid,
                        });

                        setToastMessage('Email sent and request approved successfully');
                        setToastType('success');
                        setShowToast(true);

                        // Refresh requests
                        const requestsSnap = await getDocs(
                            query(
                                collection(db, 'banks', companyId, 'hardware_requests'),
                                orderBy('createdAt', 'desc')
                            )
                        );
                        const requestsList = requestsSnap.docs
                            .map(doc => ({
                                id: doc.id,
                                ...doc.data()
                            }))
                            .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
                        setRequests(requestsList);

                        // Update selected request
                        const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
                        if (updatedRequest) {
                            setSelectedRequest(updatedRequest);
                            setEditedRequest(updatedRequest);
                        }
                    } else {
                        setToastMessage('Email sent successfully');
                        setToastType('success');
                        setShowToast(true);
                    }

                    // Close modals
                    setShowEmailModal(false);
                    setEmailRecipients([]);
                    setEmailInput('');
                    setIsActionLoading(false);
                } catch (error) {
                    console.error('Error approving request:', error);
                    setToastMessage('Email sent but failed to approve request');
                    setToastType('warning');
                    setShowToast(true);
                }
            } else {
                if (results.successful.length > 0) {
                    setToastMessage(resultMessage || 'Email sent successfully');
                    setToastType('success');
                    setShowToast(true);
                    // Close modals on success
                    setShowEmailModal(false);
                    setEmailRecipients([]);
                    setEmailInput('');
                } else {
                    setToastMessage(resultMessage || 'Failed to send email');
                    setToastType('error');
                    setShowToast(true);
                }
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setToastMessage('Failed to send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleMarkAsInstalled = async () => {
        if (!companyId || !selectedRequest || !uid) return;

        if (!installationDate) {
            setToastMessage('Please enter installation date');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                psoStatus: 'installed',
                installationDate: installationDate,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Hardware marked as installed successfully');
            setToastType('success');
            setShowToast(true);
            setShowInstallConfirmModal(false);
            setInstallationDate(new Date().toISOString().split('T')[0]);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error marking as installed:', error);
            setToastMessage('Failed to mark as installed');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleMarkAsNotInstalled = async () => {
        if (!companyId || !selectedRequest || !uid) return;

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                psoStatus: 'not installed',
                installationDate: null,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('PSO status updated to Not Installed');
            setToastType('success');
            setShowToast(true);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error updating PSO status:', error);
            setToastMessage('Failed to update PSO status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
            setProcessingAction(null);
        }
    };

    const handleDownloadEmailPDF = async (request: CCTVRequest) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setProcessingAction({ id: request.id, type: 'pdf' });
        try {
            // Get vendor information - try global companies collection first, then bank's companies collection
            let selectedVendor: any = null;
            if (request.vendorId) {
                try {
                    console.log('🔍 Attempting PDF download for vendorId:', request.vendorId);

                    // 1. Try global companies collection by direct ID first
                    const globalVendorDoc = await getDoc(doc(db, 'companies', request.vendorId));
                    if (globalVendorDoc.exists()) {
                        selectedVendor = { id: globalVendorDoc.id, ...globalVendorDoc.data() };
                        console.log('✅ PDF: Found global vendor by direct ID:', selectedVendor);
                    } else {
                        // 2. Try global companies collection by companyId field (e.g. DL-0001)
                        const globalQuery = query(collection(db, 'companies'), where('companyId', '==', request.vendorId));
                        const globalSnap = await getDocs(globalQuery);

                        if (!globalSnap.empty) {
                            selectedVendor = { id: globalSnap.docs[0].id, ...globalSnap.docs[0].data() };
                            console.log('✅ PDF: Found global vendor by companyId field:', selectedVendor);
                        } else if (companyId) {
                            // 3. Try bank-specific companies collection by direct ID
                            const bankVendorDoc = await getDoc(doc(db, 'banks', companyId, 'companies', request.vendorId));
                            if (bankVendorDoc.exists()) {
                                selectedVendor = { id: bankVendorDoc.id, ...bankVendorDoc.data() };
                                console.log('✅ PDF: Found bank vendor by direct ID:', selectedVendor);
                            } else {
                                // 4. Try bank-specific companies collection by companyId field
                                const bankQuery = query(collection(db, 'banks', companyId, 'companies'), where('companyId', '==', request.vendorId));
                                const bankSnap = await getDocs(bankQuery);

                                if (!bankSnap.empty) {
                                    selectedVendor = { id: bankSnap.docs[0].id, ...bankSnap.docs[0].data() };
                                    console.log('✅ PDF: Found bank vendor by companyId field:', selectedVendor);
                                }
                            }
                        }
                    }

                    if (!selectedVendor) {
                        console.log('❌ PDF: Vendor not found in any collection for ID:', request.vendorId);
                    }
                } catch (error) {
                    console.error('Error fetching vendor:', error);
                }
            }

            // Get branch PSO information
            let branchPsoName: string = '';
            let branchPsoRegion: string = '';

            if (request.branchId && companyId) {
                try {
                    const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', request.branchId));
                    if (branchDoc.exists()) {
                        const branchData = branchDoc.data();
                        const branchPsoId = branchData.psoId;

                        if (branchPsoId && typeof branchPsoId === 'string') {
                            const branchPsoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', branchPsoId));
                            if (branchPsoDoc.exists()) {
                                const branchPsoData = branchPsoDoc.data();
                                branchPsoName = branchPsoData.name || '';

                                // Extract region from designation
                                const designation = branchPsoData.designation || '';
                                if (designation.includes('North')) {
                                    branchPsoRegion = 'North Region';
                                } else if (designation.includes('South')) {
                                    branchPsoRegion = 'South Region';
                                } else if (designation.includes('East')) {
                                    branchPsoRegion = 'East Region';
                                } else if (designation.includes('West')) {
                                    branchPsoRegion = 'West Region';
                                } else if (designation.includes('Central')) {
                                    branchPsoRegion = 'Central Region';
                                } else {
                                    branchPsoRegion = designation || 'Region';
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branch PSO:', error);
                }
            }

            // Helper to convert image to base64 to avoid CORS issues in PDF
            const convertUrlToBase64 = async (url: string): Promise<string> => {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    console.warn('Failed to convert image to base64:', error);
                    return url; // Return original URL if conversion fails
                }
            };

            // Fetch full bank data and convert logo
            let bankData: any = null;
            if (companyId) {
                try {
                    const bankDoc = await getDoc(doc(db, 'banks', companyId));
                    if (bankDoc.exists()) {
                        bankData = bankDoc.data();

                        // Convert logo to base64 if it exists
                        if (bankData?.imgUrl) {
                            console.log('🔄 Converting bank logo to Base64 for PDF...');
                            try {
                                const base64Logo = await convertUrlToBase64(bankData.imgUrl);
                                bankData = { ...bankData, imgUrl: base64Logo };
                                console.log('✅ Logo converted successfully');
                            } catch (e) {
                                console.error('Error converting logo:', e);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching bank data:', error);
                }
            }

            // Generate email HTML
            let emailBody: string;

            if (usePurchaseOrderTemplate) {
                // For incharge and bank users, use purchase order format
                const poNumber = request.poNumber || request.requestId || generatePurchaseOrderNumber();
                const poDate = request.createdAt?.toDate ? request.createdAt.toDate() : new Date();

                // Get incharge info
                const inchargeInfo = {
                    name: psoName || (isBankUser ? 'Bank User' : 'Incharge'),
                    email: pso?.email || (isBankUser ? auth.currentUser?.email : '') || ''
                };

                emailBody = formatCCTVPurchaseOrderEmail(request, selectedVendor, branches, inchargeInfo, poNumber, poDate, inventoryItems, branchPsoName, branchPsoRegion, taxPercentage, bankData);
            } else {
                // For regular PSO/Regional, use regular email format
                emailBody = formatCCTVEmailBody(request, selectedVendor, branches, bankData);
            }


            // Use full email body for continuous flow
            // Note: We are no longer splitting into parts

            // Helper function to add page number to PDF
            const addPageNumber = (pdf: jsPDF, totalPages: number, pageHeight: number) => {
                const pageCount = pdf.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    pdf.setPage(i);
                    pdf.setFontSize(10);
                    pdf.setTextColor(0, 0, 0);
                    pdf.setFont('helvetica', 'normal');
                    // Add page number at bottom center
                    const pageText = `Page ${i} of ${totalPages}`;
                    const textWidth = pdf.getTextWidth(pageText);
                    const pageWidth = pdf.internal.pageSize.getWidth();
                    pdf.text(pageText, (pageWidth - textWidth) / 2, pageHeight - 10);
                }
            };

            // Helper function to render HTML section to PDF
            const renderSectionToPDF = async (htmlContent: string, html2canvas: any, pdf: jsPDF, imgWidth: number, pageHeight: number, startNewPage: boolean = false) => {
                const iframe = document.createElement('iframe');
                iframe.style.position = 'absolute';
                iframe.style.left = '-9999px';
                iframe.style.width = '800px';
                iframe.style.border = 'none';
                document.body.appendChild(iframe);

                await new Promise((resolve) => {
                    iframe.onload = resolve;
                    iframe.srcdoc = htmlContent;
                });

                await new Promise(resolve => setTimeout(resolve, 500));

                const iframeBody = iframe.contentDocument?.body || iframe.contentWindow?.document.body;
                if (!iframeBody) {
                    document.body.removeChild(iframe);
                    throw new Error('Could not access iframe content');
                }

                const canvas = await html2canvas(iframeBody, {
                    scale: 3,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    width: 800,
                    windowWidth: 800,
                    allowTaint: false,
                    foreignObjectRendering: true
                });

                document.body.removeChild(iframe);

                const imgData = canvas.toDataURL('image/png', 1.0);
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight;
                let position = 0;

                if (startNewPage) {
                    pdf.addPage();
                }

                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pageHeight;

                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                    heightLeft -= pageHeight;
                }
            };

            // Try to use html2canvas if available
            try {
                // Dynamic import for html2canvas
                const html2canvasModule = await import('html2canvas');
                const html2canvas = html2canvasModule.default || html2canvasModule;
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210; // A4 width in mm
                const pageHeight = 297; // A4 height in mm

                // Detect split markers and render in parts for clean page breaks
                if (emailBody.includes('<!-- PDF_SPLIT_TERMS -->')) {
                    const parts = emailBody.split('<!-- PDF_SPLIT_TERMS -->');

                    for (let i = 0; i < parts.length; i++) {
                        const sectionHTML = `
                            <!DOCTYPE html>
                            <html>
                            <head>
                                <meta charset="UTF-8">
                                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                <style>
                                    body { font-family: Arial, sans-serif; line-height: 1.4; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
                                    * { color: #000000 !important; }
                                    .outer-wrapper { padding: 0; margin: 0; width: 100%; }
                                    .container { width: 100%; padding: 20px; box-sizing: border-box; }
                                    .letterhead { display: flex; align-items: center; border-bottom: 2px solid #008248; padding-bottom: 10px; margin-bottom: 40px; }
                                    .bank-logo { height: 40px; margin-right: 15px; }
                                    .bank-info { flex-grow: 1; text-align: center; }
                                    .report-title { text-align: center; margin: 0; }
                                    .report-title h1 { margin: 0; font-size: 16px; font-weight: bold; text-decoration: underline; }
                                    .branch-subtitle { font-size: 13px; margin-top: 3px; font-weight: bold; }
                                    .info-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; table-layout: fixed; }
                                    .info-table td { padding: 6px 10px; border: 1px solid #000; vertical-align: top; font-size: 11px; line-height: 1.3; }
                                    .label-cell { background-color: #f7f7f7; font-weight: bold; width: 22%; }
                                    .value-cell { width: 28%; word-break: break-word; white-space: pre-wrap; }
                                    .full-value { width: 78%; word-break: break-word; white-space: pre-wrap; }
                                    .items-section h3 { margin: 15px 0 8px 0; font-size: 14px; font-weight: bold; color: #008248 !important; }
                                    .items-table { width: 100%; border-collapse: collapse; table-layout: auto; }
                                    .items-table th { background-color: #f7f7f7; font-weight: bold; padding: 8px 6px; text-align: left; border: 1px solid #000; font-size: 10px; }
                                    .items-table td { padding: 8px 6px; border: 1px solid #000; font-size: 10px; vertical-align: top; }
                                    .totals-table { width: 100%; max-width: 300px; margin-left: auto; border-collapse: collapse; margin-top: 10px; }
                                    .totals-table td { padding: 6px 10px; border: 1px solid #000; font-size: 11px; }
                                    .totals-label { font-weight: bold; text-align: right; background-color: #f7f7f7; }
                                    .totals-value { text-align: right; }
                                    .grand-total-row { font-weight: bold; background-color: #eee; }
                                    .section-title { font-weight: bold; font-size: 12px; margin: 15px 0 8px 0; text-decoration: underline; color: #008248 !important; }
                                    .list-content { margin: 0; padding-left: 18px; font-size: 10.5px; line-height: 1.5; }
                                    .list-content li { margin-bottom: 3px; }
                                    .footer-content { margin-top: 20px; font-size: 11px; line-height: 1.5; }
                                    .signature-area { margin-top: 35px; border-top: 1px solid #000; width: 200px; padding-top: 5px; }
                                </style>
                            </head>
                            <body>
                                <div class="outer-wrapper">
                                    ${parts[i]}
                                </div>
                            </body>
                            </html>
                        `;
                        await renderSectionToPDF(sectionHTML, html2canvas, pdf, imgWidth, pageHeight, i > 0);
                    }
                } else {
                    // Create wrapper HTML for the entire document (Continuous flow) - original logic
                    const fullReportHTML = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <style>
                                body { font-family: Arial, sans-serif; line-height: 1.4; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
                                * { color: #000000 !important; }
                                .outer-wrapper { padding: 0; margin: 0; width: 100%; }
                                .container { width: 100%; padding: 20px; box-sizing: border-box; }
                                .letterhead { display: flex; align-items: center; border-bottom: 2px solid #008248; padding-bottom: 10px; margin-bottom: 40px; }
                                .bank-logo { height: 40px; margin-right: 15px; }
                                .bank-info { flex-grow: 1; text-align: center; }
                                .report-title { text-align: center; margin: 0; }
                                .report-title h1 { margin: 0; font-size: 16px; font-weight: bold; text-decoration: underline; }
                                .branch-subtitle { font-size: 13px; margin-top: 3px; font-weight: bold; }
                                .info-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; table-layout: fixed; }
                                .info-table td { padding: 6px 10px; border: 1px solid #000; vertical-align: top; font-size: 11px; line-height: 1.3; }
                                .label-cell { background-color: #f7f7f7; font-weight: bold; width: 22%; }
                                .value-cell { width: 28%; word-break: break-word; white-space: pre-wrap; }
                                .full-value { width: 78%; word-break: break-word; white-space: pre-wrap; }
                                .items-section h3 { margin: 15px 0 8px 0; font-size: 14px; font-weight: bold; color: #008248 !important; }
                                .items-table { width: 100%; border-collapse: collapse; table-layout: auto; }
                                .items-table th { background-color: #f7f7f7; font-weight: bold; padding: 8px 6px; text-align: left; border: 1px solid #000; font-size: 10px; }
                                .items-table td { padding: 8px 6px; border: 1px solid #000; font-size: 10px; vertical-align: top; }
                                .totals-table { width: 100%; max-width: 300px; margin-left: auto; border-collapse: collapse; margin-top: 10px; }
                                .totals-table td { padding: 6px 10px; border: 1px solid #000; font-size: 11px; }
                                .totals-label { font-weight: bold; text-align: right; background-color: #f7f7f7; }
                                .totals-value { text-align: right; }
                                .grand-total-row { font-weight: bold; background-color: #eee; }
                                .section-title { font-weight: bold; font-size: 12px; margin: 15px 0 8px 0; text-decoration: underline; color: #008248 !important; }
                                .list-content { margin: 0; padding-left: 18px; font-size: 10.5px; line-height: 1.5; }
                                .list-content li { margin-bottom: 3px; }
                                .footer-content { margin-top: 20px; font-size: 11px; line-height: 1.5; }
                                .signature-area { margin-top: 35px; border-top: 1px solid #000; width: 200px; padding-top: 5px; }
                            </style>
                        </head>
                        <body>
                            <div class="outer-wrapper">
                                ${emailBody}
                            </div>
                        </body>
                        </html>
                    `;

                    // Render entire report
                    await renderSectionToPDF(fullReportHTML, html2canvas, pdf, imgWidth, pageHeight, false);
                }

                // Add page numbers to all pages
                const totalPages = pdf.getNumberOfPages();
                addPageNumber(pdf, totalPages, pageHeight);

                const fileName = `CCTV_Request_${request.poNumber || request.requestId || request.id}_${new Date().toISOString().split('T')[0]}.pdf`;
                pdf.save(fileName);

                setToastMessage('PDF downloaded successfully');
                setToastType('success');
                setShowToast(true);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setToastMessage('Failed to generate PDF. Please try again.');
                setToastType('error');
                setShowToast(true);
            }
        } catch (error) {
            console.error('Error downloading PDF:', error);
            setToastMessage('Failed to download PDF');
            setToastType('error');
            setShowToast(true);
        } finally {
            setProcessingAction(null);
        }
    };

    const handleSaveEdit = async () => {
        if (!companyId || !editedRequest || !selectedRequest || !uid) return;

        if (!editedRequest.branchCode || !editedRequest.branchName || !editedRequest.vendor || !editedRequest.items || editedRequest.items.length === 0) {
            setToastMessage('Please fill in all required fields and add at least one item');
            setToastType('error');
            setShowToast(true);
            return;
        }

        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                dateOfRequest: editedRequest.dateOfRequest,
                branchCode: editedRequest.branchCode,
                branchName: editedRequest.branchName,
                branchId: editedRequest.branchId,
                vendor: editedRequest.vendor,
                vendorId: editedRequest.vendorId,
                coveredArea: editedRequest.coveredArea || '',
                floorsConsistOn: editedRequest.floorsConsistOn || '',
                items: editedRequest.items,
                remarks: editedRequest.remarks,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request updated successfully');
            setToastType('success');
            setShowToast(true);
            setIsEditing(false);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'CCTV') as CCTVRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error updating request:', error);
            setToastMessage('Failed to update request');
            setToastType('error');
            setShowToast(true);
        }
    };

    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const getStatusBadge = (status?: string) => {
        if (!status) return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">N/A</span>;

        const statusLower = status.toLowerCase();
        if (statusLower.includes('po created') || statusLower.includes('po_created')) {
            return <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">PO Created</span>;
        } else if (statusLower.includes('pending')) {
            return <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">Pending</span>;
        } else if (statusLower.includes('approved') || statusLower.includes('completed')) {
            return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Approved</span>;
        } else if (statusLower.includes('rejected')) {
            return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Rejected</span>;
        } else if (statusLower.includes('cancelled')) {
            return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Cancelled</span>;
        } else if (statusLower.includes('progress')) {
            return <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">In Progress</span>;
        } else if (statusLower.includes('forwarded')) {
            return <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">Forwarded</span>;
        }
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">{status}</span>;
    };

    const getPSOStatusBadge = (psoStatus?: string, requestStatus?: string) => {
        if (requestStatus === 'cancelled') {
            return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Cancelled</span>;
        }
        if (!psoStatus || psoStatus === 'not installed') {
            return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Not Installed</span>;
        } else if (psoStatus === 'installed') {
            return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Installed</span>;
        }
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Not Installed</span>;
    };

    const exportToExcel = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No hardware requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const dataToExport: any[] = [];

        filteredRequests.forEach((request, index) => {
            // Add Request Header Row
            dataToExport.push({
                'Type': 'REQUEST',
                'PO Number': request.poNumber || 'N/A',
                'Request ID': request.requestId || request.id,
                'Date': request.dateOfRequest || 'N/A',
                'Branch Code': request.branchCode || 'N/A',
                'Branch Name': request.branchName || 'N/A',
                'Vendor': request.vendor || 'N/A',
                'Category': request.requestCategory || 'N/A',
                'Status': request.status || 'N/A',
                'PSO Status': request.psoStatus || 'Not Installed',
                'Remarks': request.remarks || 'N/A',
                'Item Code': '',
                'Item Name': '',
                'Quantity': '',
                'Unit Price': '',
                'Total Price': '',
                'Tax %': '',
                'Tax Amount': '',
                'Price with Tax': ''
            });

            // Add Items
            const items = request.items && Array.isArray(request.items) && request.items.length > 0 ? request.items : null;

            if (items) {
                items.forEach((item: any) => {
                    const quantity = item.quantity || 0;

                    // Look up the rate from inventoryItems using itemId
                    const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                    const rate = inventoryItem?.rates || 0;

                    const unitPrice = rate;
                    const totalPrice = unitPrice * quantity;
                    const taxPercent = taxPercentage || 18;
                    const taxAmount = (totalPrice * taxPercent) / 100;
                    const priceWithTax = totalPrice + taxAmount;

                    dataToExport.push({
                        'Type': 'ITEM',
                        'PO Number': '',
                        'Request ID': '',
                        'Date': '',
                        'Branch Code': '',
                        'Branch Name': '',
                        'Vendor': '',
                        'Category': '',
                        'Status': '',
                        'PSO Status': '',
                        'Remarks': '',
                        'Item Code': item.itemCode || 'N/A',
                        'Item Name': item.itemName || 'N/A',
                        'Quantity': quantity,
                        'Unit Price': `Rs. ${unitPrice.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Total Price': `Rs. ${totalPrice.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Tax %': `${taxPercent}%`,
                        'Tax Amount': `Rs. ${taxAmount.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Price with Tax': `Rs. ${priceWithTax.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    });
                });
            } else {
                dataToExport.push({
                    'Type': 'ITEM',
                    'PO Number': '',
                    'Request ID': '',
                    'Date': '',
                    'Branch Code': '',
                    'Branch Name': '',
                    'Vendor': '',
                    'Category': '',
                    'Status': '',
                    'PSO Status': '',
                    'Remarks': '',
                    'Item Code': 'N/A',
                    'Item Name': 'N/A',
                    'Quantity': 0,
                    'Unit Price': 'Rs. 0.00',
                    'Total Price': 'Rs. 0.00',
                    'Tax %': '0%',
                    'Tax Amount': 'Rs. 0.00',
                    'Price with Tax': 'Rs. 0.00'
                });
            }

            // Add empty row between requests (except after last one)
            if (index < filteredRequests.length - 1) {
                dataToExport.push({
                    'Type': '',
                    'PO Number': '',
                    'Request ID': '',
                    'Date': '',
                    'Branch Code': '',
                    'Branch Name': '',
                    'Vendor': '',
                    'Category': '',
                    'Status': '',
                    'PSO Status': '',
                    'Remarks': '',
                    'Item Code': '',
                    'Item Name': '',
                    'Quantity': '',
                    'Unit Price': '',
                    'Total Price': '',
                    'Tax %': '',
                    'Tax Amount': '',
                    'Price with Tax': ''
                });
            }
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'CCTV Requests');

        const fileName = `CCTV_Requests_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const exportToPDF = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No hardware requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(20);
        doc.text('CCTV Hardware Requests', 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);

        const tableData = filteredRequests.map(request => [
            request.poNumber || request.requestId || 'N/A',
            request.dateOfRequest || 'N/A',
            request.branchCode || 'N/A',
            request.branchName || 'N/A',
            request.vendor || 'N/A',
            request.status || 'N/A',
            request.psoStatus || 'Not Installed'
        ]);

        autoTable(doc, {
            head: [['PO Number', 'Date', 'Branch Code', 'Branch Name', 'Vendor', 'Status', 'PSO Status']],
            body: tableData,
            startY: 30,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [52, 152, 219] }
        });

        const fileName = `CCTV_Requests_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Calculate pagination
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const paginatedData = filteredRequests.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const columns: Column<CCTVRequest>[] = [
        {
            header: 'PO Number',
            accessor: 'poNumber',
            render: (request) => request.poNumber || request.requestId || 'N/A',
        },
        {
            header: 'Date of Request',
            accessor: 'dateOfRequest',
            render: (request) => request.dateOfRequest ? formatDate(new Date(request.dateOfRequest)) : 'N/A',
        },
        {
            header: 'Branch Code',
            accessor: 'branchCode',
            render: (request) => request.branchCode || 'N/A',
        },
        {
            header: 'Branch Name',
            accessor: 'branchName',
            render: (request) => request.branchName || 'N/A',
        },
        {
            header: 'Vendor',
            accessor: 'vendor',
            render: (request) => request.vendor || 'N/A',
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (request) => getStatusBadge(request.status),
        },
        {
            header: 'PSO Status',
            accessor: 'psoStatus',
            render: (request) => getPSOStatusBadge(request.psoStatus, request.status),
        },
        {
            header: 'Actions',
            accessor: 'id',
            align: 'right',
            render: (request) => (
                <div className="flex justify-end gap-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); handleApprove(request); }}
                        className="rounded-lg p-2 text-green-600 transition hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 disabled:opacity-50"
                        title="Send Email"
                        disabled={processingAction?.id === request.id}
                    >
                        {processingAction?.id === request.id && processingAction?.type === 'email' ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-green-500 border-r-transparent"></div>
                        ) : (
                            <Icon icon={FiMail} className="h-5 w-5" />
                        )}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadEmailPDF(request); }}
                        className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                        title="Download Email as PDF"
                        disabled={processingAction?.id === request.id}
                    >
                        {processingAction?.id === request.id && processingAction?.type === 'pdf' ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-red-500 border-r-transparent"></div>
                        ) : (
                            <Icon icon={FiFileText} className="h-5 w-5" />
                        )}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleViewDetails(request); }}
                        className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-5 w-5" />
                    </button>
                    {isBankUser && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRequest(request);
                            }}
                            disabled={isActionLoading}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                            title="Delete Request"
                        >
                            <Icon icon={FiTrash2} className="h-5 w-5" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const headerActions = (
        <div className="flex flex-wrap gap-3">
            {canCreateRequests && (
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Request
                </Button>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Search
                </label>
                <TextField
                    type="text"
                    placeholder="Search by PO Number, branch, vendor..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset page on search
                    }}
                    prefix={<Icon icon={FiSearch} className="h-4 w-4" />}
                />
            </div>
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Filter by Status
                </label>
                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1); // Reset page on filter change
                    }}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Statuses</option>
                    {statuses.map(status => (
                        <option key={status} value={status} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                            {status}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Filter by Date
                </label>
                <select
                    value={dateFilter}
                    onChange={(e) => {
                        setDateFilter(e.target.value);
                        setCurrentPage(1); // Reset page on filter change
                    }}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                    <option value="Daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                    <option value="Weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                    <option value="Monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                    <option value="Custom" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Custom Range</option>
                </select>
            </div>
            {dateFilter === 'Custom' && (
                <>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Start Date
                        </label>
                        <TextField
                            type="date"
                            value={startDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setStartDate(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            End Date
                        </label>
                        <TextField
                            type="date"
                            value={endDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEndDate(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );

    const handleInitiatePO = async (psoReq: any) => {
        setActiveTab('po');
        setReferencedPsoRequest(psoReq);
        
        const generatedPo = await generatePoNumber(psoReq.branchCode || '');

        setFormData({
            requestId: generateRequestId(),
            poNumber: generatedPo,
            dateOfRequest: new Date().toISOString().split('T')[0],
            branchCode: psoReq.branchCode || '',
            branchName: psoReq.branchName || '',
            branchId: psoReq.branchId || '',
            vendor: psoReq.vendor || '',
            vendorId: psoReq.vendorId || '',
            selectedItems: (psoReq.items || []).map((item: any) => ({
                itemId: item.itemId || '',
                itemCode: item.itemCode || 'N/A',
                itemName: item.itemName || 'N/A',
                itemDescription: item.specification || item.itemDescription || '',
                quantity: Number(item.quantity) || 1
            })),
            remarks: `Prefilled from approved PSO request ${psoReq.requestId}. Remarks: ${psoReq.remarks || ''}`,
            requestCategory: psoReq.requestCategory || 'New',
        });
        
        setShowAddModal(true);
    };

    const handleSelectApprovedPsoRequest = async (psoReq: any) => {
        setReferencedPsoRequest(psoReq);
        const generatedPo = await generatePoNumber(psoReq.branchCode || '');
        setFormData(prev => ({
            ...prev,
            branchCode: psoReq.branchCode || '',
            branchName: psoReq.branchName || '',
            branchId: psoReq.branchId || '',
            vendor: psoReq.vendor || prev.vendor || '',
            vendorId: psoReq.vendorId || prev.vendorId || '',
            poNumber: generatedPo,
            selectedItems: (psoReq.items || []).map((item: any) => ({
                itemId: item.itemId || '',
                itemCode: item.itemCode || 'N/A',
                itemName: item.itemName || 'N/A',
                itemDescription: item.specification || item.itemDescription || '',
                quantity: Number(item.quantity) || 1
            })),
            remarks: `Prefilled from approved PSO request ${psoReq.requestId}. ${prev.remarks || ''}`,
            requestCategory: psoReq.requestCategory || prev.requestCategory || 'New',
        }));
        setShowPsoSelectModal(false);
    };

    const fetchApprovedPsoRequests = async () => {
        if (!companyId) return;
        try {
            const snap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    where('status', '==', 'approved'),
                    where('docType', '==', 'pso_request'),
                    where('requestType', '==', 'CCTV')
                )
            );
            const list = snap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter((r: any) => !r.isPOGenerated);
            setApprovedPsoRequests(list);
        } catch (error) {
            console.error("Error fetching approved PSO requests:", error);
        }
    };

    const filteredApprovedPsoRequests = approvedPsoRequests.filter(r => {
        if (!psoSearchTerm) return true;
        const term = psoSearchTerm.toLowerCase();
        return r.requestId?.toLowerCase().includes(term) || r.branchCode?.toLowerCase().includes(term) || r.branchName?.toLowerCase().includes(term);
    });

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">CCTV Hardware Request</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage and track CCTV hardware requests</p>
            </div>

            {!isPSOUser && (
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('pso')}
                        className={`pb-4 px-6 font-semibold text-sm transition-all border-b-2 ${
                            activeTab === 'pso'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-bold'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        PSO Requests
                    </button>
                    <button
                        onClick={() => setActiveTab('po')}
                        className={`pb-4 px-6 font-semibold text-sm transition-all border-b-2 ${
                            activeTab === 'po'
                                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-bold'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        PO Requests
                    </button>
                </div>
            )}

            {activeTab === 'po' ? (
                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage="No CCTV requests found"
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            ) : (
                <CCTVPsoRequest
                    companyId={companyId}
                    branches={branches}
                    vendors={vendors}
                    deployments={deployments}
                    uid={uid}
                    pso={pso}
                    onInitiatePO={handleInitiatePO}
                />
            )}


            {/* Details/Edit Modal */}
            {showDetailsModal && selectedRequest && editedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                        setEditedRequest(null);
                        setIsEditing(false);
                        setShowBranchModal(false);
                        setShowVendorModal(false);
                    }}
                    title={isEditing ? `Edit CCTV Request - ${selectedRequest.poNumber || selectedRequest.requestId || selectedRequest.id}` : `CCTV Request Details - ${selectedRequest.poNumber || selectedRequest.requestId || selectedRequest.id}`}
                    size="lg"
                >
                    <div className="p-6">
                        {!isEditing && (
                            <div className="mb-4 flex justify-end gap-2">
                                {isActionLoading && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent"></div>
                                        Processing...
                                    </div>
                                )}
                                {isBankUser && selectedRequest.status === 'pending' && (
                                    <Button
                                        variant="primary"
                                        onClick={handleMarkAsPoCreated}
                                        disabled={isActionLoading}
                                        className="flex items-center gap-2 border-2 border-indigo-500 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Icon icon={FiCheck} className="h-4 w-4" />
                                        Mark as PO Created
                                    </Button>
                                )}
                                {canApproveReject && selectedRequest.status === 'pending' && (
                                    <>
                                        <Button
                                            variant="primary"
                                            onClick={() => handleApprove(selectedRequest)}
                                            disabled={isActionLoading}
                                            className="flex items-center gap-2 border-2 border-green-500 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isActionLoading ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                                    Approving...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiCheck} className="h-4 w-4" />
                                                    Approve
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={handleReject}
                                            disabled={isActionLoading}
                                            className="flex items-center gap-2 border-2 border-red-500 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isActionLoading ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                                    Rejecting...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiX} className="h-4 w-4" />
                                                    Reject
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}
                                {!canApproveReject && canEdit && selectedRequest.status === 'pending' && (
                                    <Button
                                        variant="secondary"
                                        onClick={handleEdit}
                                        disabled={isActionLoading}
                                        className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Icon icon={FiEdit2} className="h-4 w-4" />
                                        Edit
                                    </Button>
                                )}
                                {!isRegionalPSO && (selectedRequest.status === 'pending' || selectedRequest.status === 'approved') && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowCancelModal(true)}
                                        disabled={isActionLoading}
                                        className="flex items-center gap-2 border-2 border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel Request
                                    </Button>
                                )}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        PO Number
                                    </label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {isEditing ? (editedRequest.poNumber || editedRequest.requestId || 'N/A') : (selectedRequest.poNumber || selectedRequest.requestId || 'N/A')}
                                    </p>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Date of Request {isEditing && <span className="text-red-500">*</span>}
                                    </label>
                                    {isEditing ? (
                                        <TextField
                                            type="date"
                                            value={editedRequest.dateOfRequest || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setEditedRequest({ ...editedRequest, dateOfRequest: e.target.value })
                                            }
                                            required
                                        />
                                    ) : (
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedRequest.dateOfRequest ? formatDate(new Date(selectedRequest.dateOfRequest)) : 'N/A'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {isEditing ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="relative" ref={branchModalRef}>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch Code <span className="text-red-500">*</span>
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.branchCode || ''}
                                            placeholder="Click to select branch"
                                            readOnly
                                            onClick={() => setShowBranchModal(true)}
                                            required
                                        />
                                        {showBranchModal && (
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                    <TextField
                                                        type="text"
                                                        placeholder="Search branch..."
                                                        value={branchSearch}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBranchSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="max-h-48 overflow-y-auto">
                                                    {filteredBranches.length === 0 ? (
                                                        <div className="p-4 text-center text-sm text-gray-500">No branches found</div>
                                                    ) : (
                                                        filteredBranches.map((branch: any) => (
                                                            <div
                                                                key={branch.id}
                                                                onClick={() => {
                                                                    const updatedRequest: any = {
                                                                        ...editedRequest,
                                                                        branchId: branch.id,
                                                                        branchCode: branch.branchCode || '',
                                                                        branchName: branch.address || branch.name || '',
                                                                        floorsConsistOn: branch.coveredArea || branch.floorsConsistOn || branch.floorConsistOn || '',
                                                                        coveredArea: branch.coveredAreaSqFt 
                                                                            ? Number(branch.coveredAreaSqFt).toLocaleString() 
                                                                            : (branch.coveredArea && !isNaN(Number(branch.coveredArea)) 
                                                                                ? Number(branch.coveredArea).toLocaleString() 
                                                                                : ''),
                                                                    };

                                                                    // Check if branch has a deployed CCTV vendor and auto-select it
                                                                    const vendorInfo = getVendorFromBranch(branch);
                                                                    if (vendorInfo) {
                                                                        updatedRequest.vendorId = vendorInfo.vendorId;
                                                                        updatedRequest.vendor = vendorInfo.vendor;
                                                                        console.log('Auto-selected deployed CCTV vendor:', vendorInfo.vendor);
                                                                    } else {
                                                                        // No deployed vendor - clear vendor selection so user can select
                                                                        updatedRequest.vendorId = '';
                                                                        updatedRequest.vendor = '';
                                                                    }

                                                                    setEditedRequest(updatedRequest);
                                                                    setShowBranchModal(false);
                                                                    setBranchSearch('');
                                                                }}
                                                                className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                            >
                                                                <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch Name
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.branchName || ''}
                                            placeholder="Branch Name"
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Floor Consist On
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.floorsConsistOn || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setEditedRequest({ ...editedRequest, floorsConsistOn: e.target.value })
                                            }
                                            placeholder="e.g. Ground + First Floor"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch Covered Area
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.coveredArea || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const clean = e.target.value.replace(/[^\d]/g, '');
                                                setEditedRequest({ 
                                                    ...editedRequest, 
                                                    coveredArea: clean ? Number(clean).toLocaleString() : '' 
                                                });
                                            }}
                                            placeholder="e.g. 100,000"
                                            suffix={
                                                <span className="absolute right-[-6px] whitespace-nowrap inline-flex items-center rounded bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/30 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider select-none">
                                                    Square Feet
                                                </span>
                                            }
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Floor Consist On</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.floorsConsistOn || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Covered Area</label>
                                        <div className="mt-1 flex items-center gap-2">
                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const val = selectedRequest.coveredArea;
                                                    if (!val) return 'N/A';
                                                    if (/^\d+$/.test(val.toString().replace(/,/g, ''))) {
                                                        return Number(val.toString().replace(/,/g, '')).toLocaleString();
                                                    }
                                                    return val;
                                                })()}
                                            </span>
                                            {selectedRequest.coveredArea && (
                                                <span className="whitespace-nowrap inline-flex items-center rounded bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/30 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider select-none">
                                                    Square Feet
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                            {!isEditing && (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Category</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestCategory || 'N/A'}</p>
                                </div>
                            )}

                            {isEditing ? (
                                <div className="relative" ref={vendorModalRef}>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Vendor <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="text"
                                        value={editedRequest.vendor || ''}
                                        placeholder="Click to select vendor"
                                        readOnly
                                        onClick={() => setShowVendorModal(true)}
                                        required
                                    />
                                    {showVendorModal && (
                                        <div className="absolute z-50 mt-1 max-h-60 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search vendor..."
                                                    value={vendorSearch}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVendorSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {filteredVendors.length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                                ) : (
                                                    filteredVendors.map(vendor => (
                                                        <div
                                                            key={vendor.id}
                                                            onClick={() => {
                                                                setEditedRequest({
                                                                    ...editedRequest,
                                                                    // Use companyId if available (links to global companies collection), otherwise use id (from bank's companies collection)
                                                                    vendorId: vendor.companyId || vendor.id,
                                                                    vendor: vendor.companyName || '',
                                                                });
                                                                setShowVendorModal(false);
                                                                setVendorSearch('');
                                                            }}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                            {vendor.companyId && (
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">ID: {vendor.companyId}</p>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.vendor || 'N/A'}</p>
                                </div>
                            )}

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Items {isEditing && <span className="text-red-500">*</span>}
                                </label>
                                {isEditing && (
                                    <Button
                                        variant="secondary"
                                        onClick={handleOpenItemModal}
                                        className="w-full"
                                    >
                                        <Icon icon={FiPlus} className="mr-2 h-4 w-4" />
                                        Add Item
                                    </Button>
                                )}
                                {isEditing && editedRequest.items && editedRequest.items.length > 0 ? (
                                    <div className="mt-3 space-y-2">
                                        {editedRequest.items.map((item, index) => {
                                            // Find item description from inventory items if not available
                                            const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                            const itemDescription = item.itemDescription || inventoryItem?.itemDescription || '';

                                            return (
                                                <div key={index} className="flex items-start justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {item.itemCode} - {item.itemName}
                                                        </p>
                                                        {itemDescription && (
                                                            <p className="mt-1 line-clamp-1 text-xs text-gray-600 dark:text-gray-400">
                                                                {itemDescription}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="ml-4 flex shrink-0 items-center gap-2">
                                                        <label className="text-xs text-gray-600 dark:text-gray-400">Qty:</label>
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                if (qty >= 1) {
                                                                    handleQuantityChange(index, qty);
                                                                }
                                                            }}
                                                            min="1"
                                                            className="w-14"
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveItem(index)}
                                                            className="rounded-lg p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                                        >
                                                            <Icon icon={FiX} className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : !isEditing && selectedRequest.items && selectedRequest.items.length > 0 ? (
                                    <div className="mt-2 overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 dark:bg-gray-800">
                                                    <th className="border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">S. NO</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Item Name</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Qty</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Unit Price</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Price</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Tax %</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Tax Amount</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Price with Tax</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedRequest.items.map((item, index) => {
                                                    // Find item details from inventory items
                                                    const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                                    const itemDescription = item.itemDescription || inventoryItem?.itemDescription || '';
                                                    const unitPriceWithTax = inventoryItem?.rates || 0;
                                                    const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;

                                                    // Calculate pricing details
                                                    const quantity = item.quantity || 0;
                                                    const taxRate = itemTaxPercentage / 100;
                                                    const totalPriceWithTax = unitPriceWithTax * quantity;
                                                    const basePrice = totalPriceWithTax / (1 + taxRate);
                                                    const unitBasePrice = quantity > 0 ? basePrice / quantity : 0;
                                                    const tax = totalPriceWithTax - basePrice;

                                                    return (
                                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">{index + 1}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                                <div>
                                                                    <p className="font-medium">{item.itemCode} - {item.itemName}</p>
                                                                    {itemDescription && (
                                                                        <p className="text-xs text-gray-600 dark:text-gray-400">{itemDescription}</p>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">{quantity}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${unitBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${basePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `${itemTaxPercentage}%` : 'N/A'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${totalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {(() => {
                                                    // Calculate grand totals
                                                    let grandTotalBasePrice = 0;
                                                    let grandTotalTax = 0;
                                                    let grandTotalPriceWithTax = 0;

                                                    selectedRequest.items.forEach((item) => {
                                                        const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                                        const unitPriceWithTax = inventoryItem?.rates || 0;
                                                        const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;
                                                        const quantity = item.quantity || 0;

                                                        if (unitPriceWithTax > 0) {
                                                            const taxRate = itemTaxPercentage / 100;
                                                            const totalPriceWithTax = unitPriceWithTax * quantity;
                                                            const basePrice = totalPriceWithTax / (1 + taxRate);
                                                            const tax = totalPriceWithTax - basePrice;

                                                            grandTotalBasePrice += basePrice;
                                                            grandTotalTax += tax;
                                                            grandTotalPriceWithTax += totalPriceWithTax;
                                                        }
                                                    });

                                                    return (
                                                        <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
                                                            <td colSpan={3} className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Grand Total:
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                -
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                                                                -
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">No items</p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Remarks
                                </label>
                                {isEditing ? (
                                    <textarea
                                        rows={3}
                                        value={editedRequest.remarks || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            setEditedRequest({ ...editedRequest, remarks: e.target.value })
                                        }
                                        placeholder="Enter any additional remarks..."
                                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedRequest.remarks || 'N/A'}
                                    </p>
                                )}
                            </div>

                            {!isEditing && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                            <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <p className="mt-1">{getPSOStatusBadge(selectedRequest.psoStatus, selectedRequest.status)}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                        </div>
                                        {selectedRequest.psoStatus === 'installed' && selectedRequest.installationDate && (
                                            <div>
                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Installation Date</label>
                                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                    {formatDate(new Date(selectedRequest.installationDate))}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {/* PSO Status buttons - only show to requester */}
                                    {selectedRequest.createdBy === uid && selectedRequest.status !== 'cancelled' && (
                                        <div className="mt-4 space-y-2">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant={selectedRequest.psoStatus === 'installed' ? 'primary' : 'secondary'}
                                                    onClick={() => {
                                                        if (selectedRequest.psoStatus !== 'installed') {
                                                            setShowInstallConfirmModal(true);
                                                        }
                                                    }}
                                                    disabled={isActionLoading || selectedRequest.psoStatus === 'installed'}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Icon icon={FiCheck} className="h-4 w-4" />
                                                    Installed
                                                </Button>
                                                <Button
                                                    variant={selectedRequest.psoStatus === 'not installed' || !selectedRequest.psoStatus ? 'primary' : 'secondary'}
                                                    onClick={handleMarkAsNotInstalled}
                                                    disabled={isActionLoading || selectedRequest.psoStatus === 'not installed' || !selectedRequest.psoStatus}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Icon icon={FiX} className="h-4 w-4" />
                                                    Not Installed
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {selectedRequest.status === 'cancelled' && (
                                        <div className="mt-4">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <p className="mt-2">{getPSOStatusBadge(selectedRequest.psoStatus, selectedRequest.status)}</p>
                                        </div>
                                    )}
                                    {isIncharge && selectedRequest.status === 'approved' && (
                                        <div className="mt-4">
                                            <Button
                                                variant="primary"
                                                onClick={() => handleApprove(selectedRequest)}
                                                disabled={isActionLoading}
                                                className="flex items-center gap-2"
                                            >
                                                <Icon icon={FiMail} className="h-4 w-4" />
                                                Send Email
                                            </Button>
                                        </div>
                                    )}
                                    {selectedRequest.status === 'cancelled' && selectedRequest.cancelReason && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Cancellation Reason</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.cancelReason}</p>
                                        </div>
                                    )}
                                    {selectedRequest.status === 'rejected' && (selectedRequest.rejectReason || selectedRequest.declineReason) && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Rejection Reason</label>
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{selectedRequest.rejectReason || selectedRequest.declineReason}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        {isEditing && (
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        if (!editedRequest.branchCode || !editedRequest.branchName || !editedRequest.vendor || !editedRequest.items || editedRequest.items.length === 0) {
                                            setToastMessage('Please fill in all required fields and add at least one item');
                                            setToastType('error');
                                            setShowToast(true);
                                            return;
                                        }
                                        handleSaveEdit();
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Reject Request Modal */}
            {showRejectModal && (
                <Modal
                    isOpen={showRejectModal}
                    onClose={() => {
                        setShowRejectModal(false);
                        setRejectReason('');
                    }}
                    title="Reject Request"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason for Rejection <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                value={rejectReason}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
                                placeholder="Please provide a reason for rejecting this request..."
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            />
                        </div>
                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectReason('');
                                }}
                                disabled={isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmReject}
                                disabled={isActionLoading}
                                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isActionLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Rejecting...
                                    </>
                                ) : (
                                    'Confirm Reject'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Email Modal */}
            {showEmailModal && selectedRequest && (
                <Modal
                    isOpen={showEmailModal}
                    onClose={() => {
                        setShowEmailModal(false);
                        setEmailRecipients([]);
                        setEmailInput('');
                    }}
                    title="Send Email to Vendor"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Recipients <span className="text-red-500">*</span>
                            </label>
                            <div className="min-h-[60px] rounded-xl border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                                <div className="flex flex-wrap gap-2">
                                    {emailRecipients.map((email, index) => (
                                        <span
                                            key={`${email}-${index}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 break-all"
                                        >
                                            {email}
                                            <button
                                                type="button"
                                                onClick={() => removeEmailRecipient(email)}
                                                className="ml-1 rounded-full text-blue-600 hover:bg-blue-200 dark:text-blue-400 dark:hover:bg-blue-900/50"
                                            >
                                                <Icon icon={FiX} className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyDown={handleEmailInputKeyDown}
                                        placeholder="Type email and press Enter"
                                        className="flex-1 border-0 bg-transparent px-2 py-1 text-sm outline-none focus:ring-0"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Type email address and press Enter to add recipient
                            </p>
                        </div>

                        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Email Summary:</h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {isIncharge
                                    ? 'Email will include full purchase order (auto-generated PO number/date, vendor & branch details, item table with pricing, financial summary, terms & conditions, coordination note).'
                                    : 'Email will include hardware request details, branch information, and vendor instructions.'}
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setEmailRecipients([]);
                                    setEmailInput('');
                                }}
                                disabled={isSendingEmail}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSendEmail}
                                disabled={emailRecipients.length === 0 || isSendingEmail}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSendingEmail ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon={FiMail} className="mr-2 h-4 w-4" />
                                        Send Email
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Cancel Request Modal */}
            {showCancelModal && (
                <Modal
                    isOpen={showCancelModal}
                    onClose={() => {
                        setShowCancelModal(false);
                        setCancelReason('');
                    }}
                    title="Cancel Hardware Request"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason for Cancellation <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                value={cancelReason}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCancelReason(e.target.value)}
                                placeholder="Please provide a reason for cancelling this request..."
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            />
                        </div>
                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowCancelModal(false);
                                    setCancelReason('');
                                }}
                                disabled={isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                No
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCancelRequest}
                                disabled={!cancelReason.trim() || isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isActionLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Cancelling...
                                    </>
                                ) : (
                                    'Yes, Cancel Request'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Mark as Installed Confirmation Modal */}
            {showInstallConfirmModal && (
                <Modal
                    isOpen={showInstallConfirmModal}
                    onClose={() => {
                        setShowInstallConfirmModal(false);
                        setInstallationDate(new Date().toISOString().split('T')[0]);
                    }}
                    title="Confirm Installation"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Are you sure you want to mark this hardware request as installed?
                            </p>
                            {selectedRequest && (
                                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">PO Number:</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedRequest.poNumber || selectedRequest.requestId || selectedRequest.id}</p>
                                    <p className="mt-1 text-xs font-semibold text-gray-600 dark:text-gray-400">Branch:</p>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchName || 'N/A'}</p>
                                </div>
                            )}
                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Installation Date <span className="text-red-500">*</span>
                                </label>
                                <TextField
                                    type="date"
                                    value={installationDate}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInstallationDate(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowInstallConfirmModal(false);
                                    setInstallationDate(new Date().toISOString().split('T')[0]);
                                }}
                                disabled={isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                No
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    handleMarkAsInstalled();
                                }}
                                disabled={isActionLoading || !installationDate}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isActionLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Marking...
                                    </>
                                ) : (
                                    'Yes, Mark as Installed'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Request Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Add New CCTV Hardware Request"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="space-y-4">
                            {isIncharge && !referencedPsoRequest && (
                                <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-950/10 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs font-semibold text-blue-800 dark:text-blue-400">Approved PSO Request Linkage (Optional)</p>
                                            <p className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">
                                                {referencedPsoRequest 
                                                    ? `Selected Request: ${referencedPsoRequest.requestId} (Branch: ${referencedPsoRequest.branchCode})` 
                                                    : "You can select an approved PSO Request to prefill branch details and link this PO."}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                variant="secondary" 
                                                type="button"
                                                onClick={() => {
                                                    fetchApprovedPsoRequests();
                                                    setShowPsoSelectModal(true);
                                                }}
                                                className="text-xs"
                                              >
                                                {referencedPsoRequest ? "Change Request" : "Select Approved PSO"}
                                            </Button>
                                            {referencedPsoRequest && (
                                                <Button 
                                                    variant="secondary" 
                                                    type="button"
                                                    onClick={() => {
                                                        setReferencedPsoRequest(null);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            branchCode: '',
                                                            branchName: '',
                                                            branchId: '',
                                                            poNumber: '',
                                                        }));
                                                    }}
                                                    className="text-xs text-red-500 hover:text-red-600 border-red-200 hover:bg-red-50"
                                                >
                                                    Clear Link
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        PO Number <span className="text-red-500">*</span>
                                    </label>
                                    <p className="mt-1 text-sm font-bold text-blue-600 dark:text-blue-400">
                                        {formData.poNumber || 'Select branch to generate...'}
                                    </p>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Date of Request <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="date"
                                        value={formData.dateOfRequest}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setFormData(prev => ({ ...prev, dateOfRequest: e.target.value }))
                                        }
                                        required
                                        readOnly={!!referencedPsoRequest}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Request Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.requestCategory}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        setFormData(prev => ({ ...prev, requestCategory: e.target.value as 'Replacement' | 'Additional' | 'New' }))
                                    }
                                    required
                                    disabled={!!referencedPsoRequest}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Category</option>
                                    <option value="Replacement" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Replacement</option>
                                    <option value="Additional" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Additional</option>
                                    <option value="New" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">New</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative" ref={branchModalRef}>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Branch Code <span className="text-red-500">*</span>
                                    </label>
                                    {referencedPsoRequest ? (
                                        <TextField
                                            type="text"
                                            value={formData.branchCode}
                                            readOnly
                                            required
                                        />
                                    ) : (
                                        <TextField
                                            type="text"
                                            value={formData.branchCode}
                                            placeholder="Click to select branch"
                                            readOnly
                                            onClick={() => setShowBranchModal(true)}
                                            required
                                            suffix={<Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                        />
                                    )}
                                    {!referencedPsoRequest && showBranchModal && (
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search branch..."
                                                    value={branchSearch}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBranchSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {filteredBranches.length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-gray-500">No branches found</div>
                                                ) : (
                                                    filteredBranches.map((branch: any) => (
                                                        <div
                                                            key={branch.id}
                                                            onClick={() => handleBranchSelection(branch)}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Branch Name
                                    </label>
                                    <TextField
                                        type="text"
                                        value={formData.branchName}
                                        placeholder="Branch Name"
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="relative" ref={vendorModalRef}>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Vendor <span className="text-red-500">*</span>
                                </label>
                                <TextField
                                    type="text"
                                    value={formData.vendor}
                                    placeholder="Click to select vendor"
                                    readOnly
                                    onClick={() => !referencedPsoRequest && setShowVendorModal(true)}
                                    required
                                    suffix={!referencedPsoRequest && <Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                />
                                {showVendorModal && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                                        <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                            <TextField
                                                type="text"
                                                placeholder="Search vendor..."
                                                value={vendorSearch}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVendorSearch(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredVendors.length === 0 ? (
                                                <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                            ) : (
                                                filteredVendors.map(vendor => (
                                                    <div
                                                        key={vendor.id}
                                                        onClick={() => handleVendorSelection(vendor)}
                                                        className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                    >
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                        {vendor.companyId && (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">ID: {vendor.companyId}</p>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Items <span className="text-red-500">*</span>
                                </label>
                                {!referencedPsoRequest && (
                                    <Button
                                        variant="secondary"
                                        onClick={handleOpenItemModal}
                                        className="w-full"
                                    >
                                        <Icon icon={FiPlus} className="mr-2 h-4 w-4" />
                                        Add Item
                                    </Button>
                                )}

                                {/* Selected Items List */}
                                {formData.selectedItems.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {formData.selectedItems.map((item, index) => (
                                            <div key={index} className="flex items-start justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {item.itemCode} - {item.itemName}
                                                    </p>
                                                    {item.itemDescription && (
                                                        <p className="mt-1 line-clamp-1 text-xs text-gray-600 dark:text-gray-400">
                                                            {item.itemDescription}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="ml-4 flex shrink-0 items-center gap-2">
                                                    <label className="text-xs text-gray-600 dark:text-gray-400 font-semibold">Qty:</label>
                                                    {referencedPsoRequest ? (
                                                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                            {item.quantity}
                                                        </span>
                                                    ) : (
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                if (qty >= 1) {
                                                                    handleQuantityChange(index, qty);
                                                                }
                                                            }}
                                                            min="1"
                                                            className="w-16"
                                                        />
                                                    )}
                                                    {!referencedPsoRequest && (
                                                        <button
                                                            onClick={() => handleRemoveItem(index)}
                                                            className="rounded-lg p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                                        >
                                                            <Icon icon={FiX} className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>


                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Remarks
                                </label>
                                <textarea
                                    rows={3}
                                    value={formData.remarks}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        setFormData(prev => ({ ...prev, remarks: e.target.value }))
                                    }
                                    placeholder="Enter any additional remarks..."
                                    readOnly={!!referencedPsoRequest}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    if (!formData.branchCode || !formData.branchName || !formData.vendor || formData.selectedItems.length === 0 || !formData.requestCategory) {
                                        setToastMessage('Please fill in all required fields (including Request Category) and add at least one item');
                                        setToastType('error');
                                        setShowToast(true);
                                        return;
                                    }
                                    setShowConfirmModal(true);
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Request'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Items Modal */}
            {showItemModal && (
                <Modal
                    isOpen={showItemModal}
                    onClose={() => {
                        setShowItemModal(false);
                        setTempSelectedItems({});
                        setItemSearch('');
                        setItemBrandFilter('All');
                    }}
                    title="Select Items"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Filter by Brand
                                </label>
                                <select
                                    value={itemBrandFilter}
                                    onChange={(e) => setItemBrandFilter(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Brands</option>
                                    {uniqueBrands.map((brand) => (
                                        <option key={brand} value={brand} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                                            {brand}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Search Items
                                </label>
                                <TextField
                                    type="text"
                                    placeholder="Search item..."
                                    value={itemSearch}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemSearch(e.target.value)}
                                    prefix={<Icon icon={FiSearch} className="h-4 w-4" />}
                                />
                            </div>
                        </div>

                        <div className="mb-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            {availableItems.length === 0 ? (
                                <div className="p-4 text-center text-sm text-gray-500">No items available</div>
                            ) : (
                                availableItems.map((item: InventoryItem) => {
                                    const isSelected = !!tempSelectedItems[item.id];
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => handleItemToggle(item)}
                                            className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 p-4 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : ''
                                                }`}
                                        >
                                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${isSelected
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                {isSelected && <Icon icon={FiCheck} className="h-3 w-3 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                                    {item.itemCode} - {item.itemName}
                                                </p>
                                                {item.itemDescription && (
                                                    <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                                                        {item.itemDescription}
                                                    </p>
                                                )}
                                                {item.brandName && (
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                                        Brand: {item.brandName} {item.makeModel ? `- ${item.makeModel}` : ''}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {Object.keys(tempSelectedItems).length > 0 && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                                    {Object.keys(tempSelectedItems).length} item(s) selected
                                </p>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowItemModal(false);
                                    setTempSelectedItems({});
                                    setItemSearch('');
                                    setItemBrandFilter('All');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmitSelectedItems}
                                disabled={Object.keys(tempSelectedItems).length === 0}
                            >
                                Add Selected Items
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <Modal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    title="Confirm Hardware Request"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Please review the request details before submitting:
                            </h3>

                            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">PO Number:</span>
                                    <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{formData.poNumber || 'N/A'}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Branch:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.branchCode} - {formData.branchName}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Vendor:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.vendor}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Date of Request:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.dateOfRequest}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Items:</span>
                                    <div className="mt-1 space-y-1">
                                        {formData.selectedItems.map((item, index) => (
                                            <div key={index} className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs dark:bg-gray-700/50">
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {item.itemCode} - {item.itemName}
                                                </span>
                                                <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                                    Qty: {item.quantity}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            Total Items: <span className="font-bold">{formData.selectedItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                                        </p>
                                    </div>
                                </div>
                                {formData.remarks && (
                                    <div>
                                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Remarks:</span>
                                        <p className="text-sm text-gray-900 dark:text-gray-100">{formData.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    handleSaveRequest();
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Confirm & Submit'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* PSO Selection Modal */}
            {showPsoSelectModal && (
                <Modal
                    isOpen={showPsoSelectModal}
                    onClose={() => setShowPsoSelectModal(false)}
                    title="Select Approved PSO Request"
                    size="lg"
                >
                    <div className="p-6 space-y-4">
                        <TextField
                            type="text"
                            placeholder="Search by Request ID, Branch Code..."
                            value={psoSearchTerm}
                            onChange={(e: any) => setPsoSearchTerm(e.target.value)}
                        />
                        <div className="max-h-96 overflow-y-auto border rounded-xl dark:border-gray-700">
                            {filteredApprovedPsoRequests.length === 0 ? (
                                <p className="p-4 text-center text-sm text-gray-500">No approved PSO Requests found.</p>
                            ) : (
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 border-b dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                                            <th className="p-3">Request ID</th>
                                            <th className="p-3">Branch</th>
                                            <th className="p-3">PSO Name</th>
                                            <th className="p-3 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredApprovedPsoRequests.map(r => (
                                            <tr key={r.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:border-gray-700">
                                                <td className="p-3 text-sm font-semibold">{r.requestId}</td>
                                                <td className="p-3 text-sm">{r.branchCode} - {r.branchName}</td>
                                                <td className="p-3 text-sm">{r.psoName}</td>
                                                <td className="p-3 text-sm text-right">
                                                    <Button
                                                        variant="primary"
                                                        className="text-xs"
                                                        onClick={() => handleSelectApprovedPsoRequest(r)}
                                                    >
                                                        Select
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                            <Button variant="secondary" onClick={() => setShowPsoSelectModal(false)}>Close</Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Toast */}
            {showToast && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div >
    );
}

